import React from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    RadialLinearScale,
    Title,
    Tooltip,
    Legend,
    Filler,
    BubbleController
} from 'chart.js';
import { Line, Bar, Radar, Bubble } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { FaGlobe, FaBell, FaSearch } from 'react-icons/fa';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    RadialLinearScale,
    Title,
    Tooltip,
    Legend,
    Filler,
    BubbleController
);

const TyposquattingAnalysis = () => {
    // Theme colors extracted from TypoGuard theme
    const colors = {
        primary: '#0d9488', // Teal-600
        primaryAlpha: 'rgba(13, 148, 136, 0.1)',
        primaryLow: 'rgba(13, 148, 136, 0.7)',
        secondary: '#3b82f6', // Blue-500
        secondaryAlpha: 'rgba(59, 130, 246, 0.5)',
        accent: '#eab308', // Yellow-500
        accentAlpha: 'rgba(234, 179, 8, 0.7)',
        danger: '#ef4444', // Red-500
        dangerAlpha: 'rgba(239, 68, 68, 0.7)',
        purple: '#a855f7', // Purple-500
        purpleAlpha: 'rgba(168, 85, 247, 0.2)',
        darkBg: '#18181b', // Dark-900
        darkSurface: '#27272a', // Dark-800
        text: '#a1a1aa', // Dark-400
        textLight: '#e4e4e7', // Dark-200
    };

    // Chart Global Defaults
    ChartJS.defaults.color = colors.text;
    ChartJS.defaults.font.family = "'Inter', sans-serif";

    const commonOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            tooltip: {
                backgroundColor: 'rgba(24, 24, 27, 0.95)',
                titleColor: '#fff',
                bodyColor: colors.textLight,
                borderColor: colors.primary,
                borderWidth: 1,
                padding: 10,
            },
            legend: {
                labels: { color: colors.textLight }
            }
        },
        scales: {

        }
    };

    // --- DATASETS ---

    const trendData = {
        labels: ['2020', '2021', '2022', '2023', '2024', '2025 (Est.)'],
        datasets: [{
            label: 'Malicious Domains Detected (Millions)',
            data: [0.8, 1.1, 1.4, 2.1, 2.9, 3.8],
            borderColor: colors.primary,
            backgroundColor: colors.primaryAlpha,
            borderWidth: 3,
            tension: 0.4,
            fill: true,
            pointBackgroundColor: colors.darkBg,
            pointBorderColor: colors.primary,
            pointBorderWidth: 2,
            pointRadius: 5
        }]
    };

    const costData = {
        labels: [
            ['Legal &', 'Investigation Fees'],
            ['IT Productivity', 'Loss'],
            ['Lost', 'Revenue'],
            ['Brand Damage', '(Reputation)']
        ],
        datasets: [{
            label: 'Est. Cost (Thousands $)',
            data: [150, 320, 850, 1500],
            backgroundColor: [
                '#71717a', // dark-500
                '#52525b', // dark-600
                colors.accent,
                colors.danger
            ],
            borderRadius: 4
        }]
    };

    const psychologyData = {
        labels: [
            ['Visual Similarity', '(Homoglyph)'],
            'Message Urgency',
            ['Known Extension', '(.com)'],
            ['Trust Context', '(Brand)'],
            'User Fatigue'
        ],
        datasets: [{
            label: 'Phishing Success Factors',
            data: [90, 75, 60, 85, 70],
            backgroundColor: colors.purpleAlpha,
            borderColor: colors.purple,
            pointBackgroundColor: colors.purple,
            pointBorderColor: '#fff',
            pointHoverBackgroundColor: '#fff',
            pointHoverBorderColor: colors.purple
        }]
    };

    const tldData = {
        datasets: [{
            label: 'TLD Analysis',
            data: [
                { x: 12, y: 40, r: 25, label: '.com' },
                { x: 2, y: 95, r: 15, label: '.xyz' },
                { x: 5, y: 80, r: 10, label: '.top' },
                { x: 15, y: 30, r: 12, label: '.net' },
                { x: 1, y: 90, r: 18, label: '.tk' },
                { x: 45, y: 10, r: 8, label: '.bank' }
            ],
            backgroundColor: (context) => {
                const val = context.raw?.y;
                if (val > 80) return colors.dangerAlpha;
                if (val > 50) return colors.accentAlpha;
                if (val < 20) return colors.primaryLow;
                return colors.primaryLow;
            },
            borderColor: '#fafafa',
            borderWidth: 1
        }]
    };

    // Subcomponent: Fact Card
    const FactCard = ({ icon, title, text }) => (
        <div className="bg-dark-800/80 rounded-xl p-6 border border-dark-700 hover:border-primary-600 transition duration-300 shadow-lg">
            <div className="text-4xl mb-4">{icon}</div>
            <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
            <p className="text-dark-400 text-sm">{text}</p>
        </div>
    );

    // Subcomponent: Step
    const Step = ({ icon, title, subtitle, color }) => {
        const getColors = (c) => {
            switch (c) {
                case 'primary': return 'bg-primary-900 border-primary-600 shadow-[0_0_15px_rgba(13,148,136,0.5)]';
                case 'blue': return 'bg-blue-900 border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.5)]';
                case 'purple': return 'bg-purple-900 border-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.5)]';
                case 'red': return 'bg-red-900 border-red-500 shadow-[0_0_15px_rgba(239,68,68,0.5)]';
                default: return 'bg-dark-800 border-dark-600';
            }
        };

        return (
            <div className="flex flex-col items-center text-center max-w-xs z-10">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl border-2 mb-4 ${getColors(color)}`}>
                    {icon}
                </div>
                <h3 className="font-bold text-white">{title}</h3>
                <p className="text-xs text-dark-400 mt-2">{subtitle}</p>
            </div>
        );
    };

    return (
        <div className="min-h-screen flex flex-col bg-dark-900 text-dark-50 font-sans">
            <SEO
                title="Check Domain Typosquatting - Free Scanner Tool"
                description="Is your brand safe? Use our free typosquatting detection tool to scan for look-alike domains and cybersquatting attacks instantly."
                path="/analysis"
            />

            {/* Navigation - Matched from BlogArticle */}
            <nav className="w-full py-6 px-4 md:px-8 border-b border-dark-800 bg-dark-900/50 backdrop-blur-md sticky top-0 z-50">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <Link to="/" className="flex items-center space-x-2">
                        <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-400 to-primary-600">TypoGuard</span>
                    </Link>
                    <div className="space-x-4">
                        <Link to="/analysis" className="text-white hover:text-primary-400 transition-colors font-medium">Analysis</Link>
                        <Link to="/blog/typosquatting-detection-guide" className="text-dark-300 hover:text-white transition-colors">Guide</Link>
                        <Link to="/login" className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors">Sign In</Link>
                    </div>
                </div>
            </nav>

            {/* Header Title Section - Integrated into Flow */}
            <div className="w-full py-16 px-4 md:px-8 border-b border-white/5 bg-gradient-to-b from-dark-900 to-dark-800/50">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center">
                    <div className="mb-4 md:mb-0 text-center md:text-left">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-900/30 border border-primary-500/30 text-primary-400 text-sm font-mono mb-4">
                            <span>REPORT</span>
                            <span className="w-1 h-1 rounded-full bg-primary-500"></span>
                            <span>2023-2025</span>
                        </div>
                        <h1 className="text-3xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400 mb-4 leading-tight">
                            Typosquatting &<br />Brand Protection
                        </h1>
                        <p className="text-dark-300 text-xl max-w-xl">
                            The silent threat costing industries millions. An in-depth analysis of the current landscape.
                        </p>
                    </div>
                    <div className="flex gap-4">
                        <div className="text-center px-6 py-4 bg-dark-800/50 backdrop-blur-sm rounded-xl border border-red-500/20 shadow-[0_0_30px_rgba(239,68,68,0.1)]">
                            <p className="text-xs text-red-300 uppercase tracking-widest font-semibold mb-1">Threat Level</p>
                            <p className="text-3xl font-bold text-red-500">HIGH ⚠️</p>
                        </div>
                    </div>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-4 md:px-8 py-12 space-y-16">

                {/* Intro Text */}
                <section className="prose prose-invert prose-lg max-w-4xl mx-auto text-center">
                    <p className="text-xl text-dark-300 leading-relaxed">
                        Typosquatting (or URL hijacking) is no longer a simple nuisance. It is an industrial attack vector.
                        By registering domains similar to your brand (e.g., <span className="text-red-400 font-mono bg-red-900/20 px-1 rounded">goggle.com</span> instead of <span className="text-primary-400 font-mono bg-primary-900/20 px-1 rounded">google.com</span>),
                        cybercriminals intercept traffic, credentials, and revenue. Here are the critical data points to understand the urgency.
                    </p>
                </section>

                {/* Section 1: The Scale (Line Chart) */}
                <section className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
                    <div className="md:col-span-1 space-y-4">
                        <h2 className="text-2xl font-bold text-white border-l-4 border-primary-600 pl-4">1. Explosive Growth</h2>
                        <p className="text-dark-400">
                            Between 2023 and 2025, the number of malicious domains registered daily skyrocketed.
                            Automation allows attackers to generate thousands of domain variations within seconds of a new brand announcement.
                        </p>
                        <ul className="space-y-2 text-sm text-dark-300">
                            <li className="flex items-center"><span className="mr-2">📈</span> <strong>+150%</strong> increase in phishing sites.</li>
                            <li className="flex items-center"><span className="mr-2">🤖</span> AI usage to generate credible names.</li>
                        </ul>
                    </div>
                    <div className="md:col-span-2 bg-dark-800 rounded-xl p-6 shadow-glow border border-dark-600">
                        <h3 className="text-lg font-semibold mb-4 text-primary-400">Evolution of Registered Suspicious Domains (Millions)</h3>
                        <div className="h-[300px] md:h-[350px] w-full">
                            <Line
                                data={trendData}
                                options={{
                                    ...commonOptions,
                                    scales: {
                                        y: {
                                            grid: { color: 'rgba(161, 161, 170, 0.1)' },
                                            beginAtZero: true
                                        },
                                        x: {
                                            grid: { display: false }
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                </section>

                {/* Section 2: Financial Impact (Bar Chart) */}
                <section className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="bg-dark-800 rounded-xl p-6 shadow-glow border border-dark-600 order-2 md:order-1">
                        <h3 className="text-lg font-semibold mb-4 text-accent-400">Average Cost per Incident (Enterprise)</h3>
                        <div className="h-[300px] md:h-[350px] w-full">
                            <Bar
                                data={costData}
                                options={{
                                    ...commonOptions,
                                    indexAxis: 'y',
                                    scales: {
                                        x: { grid: { color: 'rgba(161, 161, 170, 0.1)' } },
                                        y: { grid: { display: false } }
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <div className="space-y-4 order-1 md:order-2 flex flex-col justify-center">
                        <h2 className="text-2xl font-bold text-white border-l-4 border-accent-500 pl-4">2. The Real Financial Impact</h2>
                        <p className="text-dark-400">
                            The cost is not limited to technical loss. Customer trust (reputation) is often the heaviest burden.
                            2024 reports indicate the total average cost of a data breach initiated by a fraudulent domain exceeds <strong>$4.5 Million</strong>.
                        </p>
                        <div className="bg-dark-800 p-4 rounded-lg border border-dark-700 mt-4">
                            <p className="font-mono text-red-400 text-sm">"Legal costs to recover a single domain via WIPO (UDRP) range between $1,500 and $5,000, excluding lawyer fees."</p>
                        </div>
                    </div>
                </section>

                {/* Section 3: User Psychology (Radar Chart) */}
                <section className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
                    <div className="md:col-span-1 space-y-4">
                        <h2 className="text-2xl font-bold text-white border-l-4 border-purple-500 pl-4">3. The Human Factor</h2>
                        <p className="text-dark-400">
                            Why does this still work in 2025? Visual fatigue and blind trust.
                            On mobile, the address bar is often truncated, making the detection of a ".co" instead of ".com" almost impossible for an untrained eye.
                        </p>
                    </div>
                    <div className="md:col-span-2 bg-dark-800 rounded-xl p-6 shadow-glow border border-dark-600">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="h-[300px] w-full">
                                <Radar
                                    data={psychologyData}
                                    options={{
                                        ...commonOptions,
                                        scales: {
                                            r: {
                                                angleLines: { color: 'rgba(161, 161, 170, 0.1)' },
                                                grid: { color: 'rgba(161, 161, 170, 0.1)' },
                                                pointLabels: {
                                                    color: colors.textLight,
                                                    font: { size: 12 }
                                                },
                                                ticks: { display: false }
                                            }
                                        }
                                    }}
                                />
                            </div>
                            <div className="flex flex-col justify-center space-y-4 p-4">
                                <div className="bg-dark-800/50 p-3 rounded border-l-2 border-purple-500">
                                    <h4 className="font-bold text-white">Homoglyph Attack</h4>
                                    <p className="text-xs text-dark-400">Replacing 'l' (lowercase L) with '1' or 'I' (uppercase i).</p>
                                </div>
                                <div className="bg-dark-800/50 p-3 rounded border-l-2 border-purple-500">
                                    <h4 className="font-bold text-white">Combo-Squatting</h4>
                                    <p className="text-xs text-dark-400">Adding credible words: "secure-bank.com" instead of "bank.com".</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Section 4: TLD Risk (Bubble Chart) */}
                <section className="space-y-6">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-white inline-block border-b-2 border-primary-600 pb-2">4. Mapping High-Risk Extensions (TLDs)</h2>
                        <p className="text-dark-400 mt-4 max-w-2xl mx-auto">
                            Some domain extensions are cheaper and less regulated, massively attracting fraudsters.
                            Bubble size = Volume of fraudulent domains. Color = Risk Level.
                        </p>
                    </div>
                    <div className="bg-dark-800 rounded-xl p-6 shadow-glow border border-dark-600 w-full">
                        <div className="h-[400px] w-full max-w-[800px] mx-auto">
                            <Bubble
                                data={tldData}
                                options={{
                                    ...commonOptions,
                                    plugins: {
                                        ...commonOptions.plugins,
                                        tooltip: {
                                            callbacks: {
                                                label: function (context) {
                                                    return `${context.raw.label}: Risk ${context.raw.y}/100`;
                                                }
                                            }
                                        },
                                        legend: { display: false }
                                    },
                                    scales: {
                                        x: {
                                            title: { display: true, text: 'Average Registration Price ($)', color: colors.text },
                                            grid: { color: 'rgba(161, 161, 170, 0.1)' }
                                        },
                                        y: {
                                            title: { display: true, text: 'Phishing Risk Score (0-100)', color: colors.text },
                                            grid: { color: 'rgba(161, 161, 170, 0.1)' },
                                            min: 0,
                                            max: 100
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                </section>

                {/* Section 5: Key Facts Cards (Grid) */}
                <section>
                    <h2 className="text-3xl font-bold text-center text-white mb-10">Did You Know? 5 Shocking Facts</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <FactCard icon="🕵️‍♂️" title="98%" text="Of the world's top 500 companies are targeted by at least one active typosquatting case." />
                        <FactCard icon="📧" title="1 in 3" text="Employees will click on a typosquatted phishing link if it appears to come from HR or IT." />
                        <FactCard icon="⏱️" title="24 Hours" text="Average time before a typosquatted domain starts receiving traffic after a product announcement." />
                        <FactCard icon="📉" title="-7% Revenue" text="Average impact on online revenue when a mirror site hijacks customer traffic." />
                        <div className="bg-dark-800/80 rounded-xl p-6 border border-dark-700 hover:border-primary-600 transition duration-300 md:col-span-2 shadow-lg">
                            <div className="flex items-start">
                                <div className="text-4xl mr-4">⚖️</div>
                                <div>
                                    <h3 className="text-xl font-bold text-white mb-2">The "Goggle.com" Case</h3>
                                    <p className="text-dark-400 text-sm">
                                        One of the most famous examples. For years, this domain hosted malware and fraudulent surveys before being recovered. Today, Twitter (X) faces "Twitter.com" vs "Twittter.com" (3 't's) used for cryptocurrency phishing.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Section 6: Anatomy of an Attack */}
                <section className="glass-panel bg-dark-800/50 rounded-2xl p-8 border border-dark-700 backdrop-blur-sm">
                    <h2 className="text-2xl font-bold text-center text-white mb-8">Anatomy of a Typosquatting Attack</h2>

                    <div className="flex flex-col md:flex-row justify-between items-center space-y-8 md:space-y-0 relative">
                        <Step icon="👀" title="1. Scouting" subtitle="Attacker identifies a popular brand or upcoming marketing campaign." color="primary" />
                        <div className="hidden md:block text-dark-600 text-3xl">➔</div>
                        <Step icon="⌨️" title="2. Registration" subtitle="Buying 50+ variants (mfa-login.com) for a few dollars." color="blue" />
                        <div className="hidden md:block text-dark-600 text-3xl">➔</div>
                        <Step icon="🎣" title="3. Weaponization" subtitle="Cloning the official site. Adding fake login forms." color="purple" />
                        <div className="hidden md:block text-dark-600 text-3xl">➔</div>
                        <Step icon="💥" title="4. Execution" subtitle="Phishing via email or passive waiting." color="red" />
                    </div>
                </section>

                {/* CTA */}
                <footer className="text-center py-12 border-t border-dark-800">
                    <h2 className="text-2xl font-bold text-white mb-4">Protect Your Digital Territory</h2>
                    <p className="text-dark-400 max-w-2xl mx-auto mb-8">
                        Proactive monitoring is the only defense. Don't let $10 domains destroy a reputation worth millions.
                    </p>
                    <Link to="/login" className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-8 rounded-full shadow-lg shadow-primary-600/30 transition transform hover:scale-105 inline-block">
                        Scan My Brand for Free
                    </Link>
                    <p className="text-xs text-dark-500 mt-12">
                        Sources: IC3 CyberCrime Reports (2023), Palo Alto Unit 42, ICANN Security Reports, 2024 Brand Protection Aggregated Data.
                    </p>
                </footer>

            </main>
        </div>
    );
};

export default TyposquattingAnalysis;
